class CartDrawer extends HTMLElement {
  constructor() {
    super();
    window.FoxTheme = window.FoxTheme || {};
    window.FoxTheme.Cart = this;
    this.drawer = this.closest("drawer-component");
    window.FoxTheme.CartDrawer = this.drawer;
    this.openWhenAdded = this.dataset.openWhenAdded === "true";

    this.onCartRefreshListener = this.onCartRefresh.bind(this);
    this.onCartScrollHandler = this.updateCartScrollIndicator.bind(this);
    this.onCartResizeHandler = this.scheduleCartScrollIndicatorUpdate.bind(this);
    this.cartScrollResizeObserver = null;
    this.cartScrollMutationObserver = null;
    this.cartDrawerMutationObserver = null;
    this.cartScrollUpdateFrame = null;
  }

  connectedCallback() {
    window.FoxThemeEvents.subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      async (state) => {
        if (state.item_count !== 0) {
          this.renderContents(
            state,
            true,
            state.source === "quick-order-list" ? false : true
          );
        }
      }
    );

    document.addEventListener("cart:refresh", this.onCartRefreshListener);
    window.addEventListener("resize", this.onCartResizeHandler);

    this.initCartScrollIndicator();
    this.scheduleCartScrollIndicatorUpdate();
  }

  disconnectedCallback() {
    document.removeEventListener("cart:refresh", this.onCartRefreshListener);
    window.removeEventListener("resize", this.onCartResizeHandler);

    if (this.scrollArea) {
      this.scrollArea.removeEventListener("scroll", this.onCartScrollHandler);
    }

    if (this.cartScrollResizeObserver) {
      this.cartScrollResizeObserver.disconnect();
    }

    if (this.cartScrollMutationObserver) {
      this.cartScrollMutationObserver.disconnect();
    }

    if (this.cartDrawerMutationObserver) {
      this.cartDrawerMutationObserver.disconnect();
    }

    if (this.cartScrollUpdateFrame) {
      cancelAnimationFrame(this.cartScrollUpdateFrame);
    }
  }

  initCartScrollIndicator() {
    const nextScrollArea = this.querySelector("[data-cart-scroll-area]");
    const nextIndicator = this.querySelector("[data-cart-scroll-indicator]");
    const nextIndicatorFill = this.querySelector("[data-cart-scroll-indicator-fill]");

    if (!nextScrollArea || !nextIndicator || !nextIndicatorFill) return;

    if (this.scrollArea && this.scrollArea !== nextScrollArea) {
      this.scrollArea.removeEventListener("scroll", this.onCartScrollHandler);
    }

    this.scrollArea = nextScrollArea;
    this.scrollIndicator = nextIndicator;
    this.scrollIndicatorFill = nextIndicatorFill;

    this.scrollArea.removeEventListener("scroll", this.onCartScrollHandler);
    this.scrollArea.addEventListener("scroll", this.onCartScrollHandler, { passive: true });

    if (this.cartScrollResizeObserver) {
      this.cartScrollResizeObserver.disconnect();
      this.cartScrollResizeObserver = null;
    }

    if (window.ResizeObserver) {
      this.cartScrollResizeObserver = new ResizeObserver(() => {
        this.scheduleCartScrollIndicatorUpdate();
      });

      this.cartScrollResizeObserver.observe(this.scrollArea);
      const cartItems = this.querySelector("[data-cart-items]");
      if (cartItems) this.cartScrollResizeObserver.observe(cartItems);
    }

    if (this.cartScrollMutationObserver) {
      this.cartScrollMutationObserver.disconnect();
      this.cartScrollMutationObserver = null;
    }

    if (window.MutationObserver) {
      this.cartScrollMutationObserver = new MutationObserver(() => {
        this.scheduleCartScrollIndicatorUpdate();
      });

      this.cartScrollMutationObserver.observe(this.scrollArea, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    if (this.drawer && !this.cartDrawerMutationObserver && window.MutationObserver) {
      this.cartDrawerMutationObserver = new MutationObserver(() => {
        this.scheduleCartScrollIndicatorUpdate();
      });

      this.cartDrawerMutationObserver.observe(this.drawer, {
        attributes: true,
        attributeFilter: ["hidden", "class", "style", "aria-hidden"],
      });
    }
  }

  scheduleCartScrollIndicatorUpdate() {
    if (this.cartScrollUpdateFrame) {
      cancelAnimationFrame(this.cartScrollUpdateFrame);
    }

    this.cartScrollUpdateFrame = requestAnimationFrame(() => {
      this.updateCartScrollIndicator();

      setTimeout(() => {
        this.updateCartScrollIndicator();
      }, 220);
    });
  }

  updateCartScrollIndicator() {
    if (!this.scrollArea || !this.scrollIndicator || !this.scrollIndicatorFill) return;

    const scrollHeight = this.scrollArea.scrollHeight;
    const clientHeight = this.scrollArea.clientHeight;
    const maxScrollTop = scrollHeight - clientHeight;

    if (clientHeight <= 0 || maxScrollTop <= 2) {
      this.scrollIndicator.classList.remove("is-visible");
      this.scrollIndicatorFill.style.height = "0px";
      this.scrollIndicatorFill.style.transform = "translateY(0px)";
      return;
    }

    this.scrollIndicator.classList.add("is-visible");

    const trackHeight = this.scrollIndicator.clientHeight;
    const fillHeight = Math.max((clientHeight / scrollHeight) * trackHeight, 28);
    const maxTranslate = Math.max(trackHeight - fillHeight, 0);
    const progress = maxScrollTop > 0 ? this.scrollArea.scrollTop / maxScrollTop : 0;
    const translateY = maxTranslate * progress;

    this.scrollIndicatorFill.style.height = `${fillHeight}px`;
    this.scrollIndicatorFill.style.transform = `translateY(${translateY}px)`;
  }

  async onCartRefresh(event) {
    const cartDrawer = document.getElementById("cart-drawer");
    if (!cartDrawer) return;

    try {
      const response = await fetch(
        `${FoxThemeSettings.routes.root}?section_id=cart-drawer`
      );
      const responseText = await response.text();

      const parser = new DOMParser();
      const parsedHTML = parser.parseFromString(responseText, "text/html");

      const newCartContent = parsedHTML.getElementById("cart-drawer").innerHTML;
      cartDrawer.innerHTML = newCartContent;

      setTimeout(() => {
        const newDrawer = cartDrawer.querySelector("drawer-component");
        if (event.detail?.open === true && !newDrawer?.isOpen()) {
          newDrawer?.openDrawer();
        }
      }, 100);

      FoxThemeCartHelpers.getState().then((state) => {
        FoxThemeCartHelpers.updateCartCount(state.item_count);
      });
    } catch (error) {
      console.error("Error refreshing cart:", error);
    }
  }

  renderContents(parsedState, renderFooter = true, isOpen = true) {
    if (parsedState.errors) return;
    this.classList.contains("is-empty") && this.classList.remove("is-empty");
    this.getSectionsToRender(renderFooter).forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    this.initCartScrollIndicator();
    this.scheduleCartScrollIndicatorUpdate();

    setTimeout(() => {
      if (this.openWhenAdded && !this.drawer.isOpen() && isOpen) {
        this.drawer.openDrawer();
        document.body.classList.add("prevent-scroll");
        window.FoxThemeEvents.emit(`ON_CART_DRAWER_OPEN`, parsedState);
        this.scheduleCartScrollIndicatorUpdate();
      }
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender(renderFooter = false) {
    const sections = [
      {
        id: "cart-drawer",
        selector: ".f-cart-drawer__items",
      },
    ];

    if (renderFooter) {
      sections.push({
        id: "cart-drawer",
        selector: ".f-drawer__footer",
      });
    }

    return sections;
  }
}
customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  initLoading() {
    this.loading = new window.FoxTheme.AnimateLoading(
      document.querySelector("cart-drawer"),
      { overlay: document.querySelector("cart-drawer") }
    );
  }

  getSectionsToRender() {
    return [
      {
        id: "Drawer-Cart",
        section: "cart-drawer",
        selector: ".f-cart-drawer__items",
      },
      {
        id: "Drawer-Cart",
        section: "cart-drawer",
        selector: ".f-cart-drawer__block-subtotal",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);